import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import type { DeepUnknown } from '@/types/deep-unknown';

import { WorkstreamTemplateSchema } from '../entities/workstream-template';

export namespace WorkstreamTemplatesQuery {
  export const key = () => ['project-management', 'workstream-templates'];

  export const OutputSchema = z.object({
    templates: z.array(
      WorkstreamTemplateSchema.pick({
        id: true,
        name: true,
        description: true,
        nbTasks: true,
      }),
    ),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    data: FeApi.ListPredefinedWorkstreamTemplatesViewOutput[],
  ): DeepUnknown<Output> {
    return {
      templates: data.map((tpl) => ({
        id: tpl.name,
        name: tpl.name,
        description: tpl.description,
        nbTasks: tpl.number_of_tasks,
      })),
    };
  }

  function useQueryFn() {
    const workstreamTemplatesList = useInjectFeApiContext(
      FeApi.tasksV1WorkstreamTemplatesListList,
    );

    return async function queryFn() {
      const templatesResult = await ok(workstreamTemplatesList());

      const output = await OutputSchema.parseAsync(
        outputMapper(templatesResult),
      );

      return output;
    };
  }

  export function useQuery() {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(),
      queryFn: () => queryFn(),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(),
        queryFn: () => queryFn(),
      });
    };
  }
}
